import React from 'react'
import { Blinker, Button } from 'src/chrome'
import { CampaignLineFragment } from 'src/graphql'
import { useActions, useCallback, useMutations, useState } from 'src/hooks'
import { CampaignShare, CreateOrderModal } from '..'

const styles = {
  campaignHeader: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-around',
    padding: '45px 0 60px 0',
    '@media (max-width: 500px)': {
      padding: '15px 0 10px',
    },
  },
  campaignActions: {
    display: 'flex',
    'div:nth-of-type(2) ': {
      marginLeft: '25px',
    },
    '@media (max-width: 500px)': {
      flexDirection: 'column' as const,
      button: {
        margin: '10px',
      },
    },
  },
}

type CampaignHeaderProps = {
  hasValidSequence: boolean
  campaignId: string
  campaignLines: CampaignLineFragment[]
  campaignName: string
  isCampaignShareable: boolean
}

const CampaignHeaderActions: React.FC<CampaignHeaderProps> = props => {
  const {
    hasValidSequence,
    campaignId,
    campaignLines,
    campaignName,
    isCampaignShareable,
  } = props

  const actions = useActions()
  const mutations = useMutations()

  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)

  const handleCreateOrder = useCallback(
    async (selectedLines: CampaignLineFragment[] | null) => {
      setIsOrderModalOpen(!isOrderModalOpen)
      const {
        createOrder: { order },
      } = await mutations.createOrder({
        order: {
          lines: selectedLines?.map(line => ({
            card: line.card?.id,
            giftVariation: line.giftVariation?.id,
            sendDelay: line.sendDelay,
          })),
          campaign: campaignId,
        },
      })
      actions.createdOrder(order)
      actions.openOrder(order.id)
    },
    [actions, isOrderModalOpen, campaignId, mutations],
  )

  const handleCreateCampaign = async (
    selectedLines: CampaignLineFragment[],
    name: string,
  ) => {
    setIsOrderModalOpen(!isOrderModalOpen)

    const lines = selectedLines.map(line => ({
      card: line.card?.id ?? null,
      giftVariation: line.giftVariation?.id ?? null,
      sendDelay: line.sendDelay,
    }))

    const {
      createCampaign: { campaign },
    } = await mutations.createCampaign({ campaign: { name, lines } })
    actions.openCampaign(campaign.id)
  }

  const handleShare = async (recipientId: string) => {
    setIsShareModalOpen(!isShareModalOpen)

    await mutations.shareCampaign({
      campaign: campaignId,
      username: recipientId,
    })
  }

  return (
    <>
      <div css={styles.campaignHeader}>
        {!hasValidSequence && (
          <Blinker
            style={{ bottom: 7 }}
            messagePosition={{
              position: 'absolute',
              top: -18,
              right: -7,
            }}
            message={
              'There must be at least one immediate card when there is a delay sequence'
            }
          />
        )}
        <div css={styles.campaignActions}>
          {isCampaignShareable && campaignLines.length > 0 && (
            <Button
              id={'share_campaign_btn'}
              title={'Share Campaign'}
              onClick={() => setIsShareModalOpen(!isShareModalOpen)}
            />
          )}
          {campaignLines.length > 0 && (
            <Button
              id={'create_order_btn'}
              title={'Send Order'}
              buttonColor={'pink'}
              onClick={() => setIsOrderModalOpen(!isOrderModalOpen)}
            />
          )}
        </div>
      </div>
      {isShareModalOpen && (
        <CampaignShare
          onClick={() => setIsShareModalOpen(!isShareModalOpen)}
          onSubmit={handleShare}
          campaignTitle={campaignName}
          lines={campaignLines}
        />
      )}
      <CreateOrderModal
        key={campaignId}
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(!isOrderModalOpen)}
        onCreateCampaign={handleCreateCampaign}
        onSubmit={handleCreateOrder}
        lines={campaignLines}
      />
    </>
  )
}

export default CampaignHeaderActions
